export { HTMLSquireEditor as default };

import Squire                  from '@/js/Shared/Editor/Squire/Squire.js';
import HTMLEditorState         from '@/js/Shared/Editor/HTMLEditorState';
import HTMLEditorOptions       from '@/js/Shared/Editor/HTMLEditorOptions';
import FilesEvent              from '@/js/Shared/Events/Entities/FilesEvent';
import DOMPurify               from 'dompurify';


/**
* Squire HTML
*
* Provides a layer over Squire HTML editor to add more functionality
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class HTMLSquireEditor
{
  static LEAFS = new Set([
    'IMG', 'HR',
  ]);

  /**
   * Squire editor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Squire}
   */
  squire;

  /**
   * Editor Options
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {HTMLEditorOptions}
   */
  options = new HTMLEditorOptions();

  /**
   * Editor State
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {HTMLEditorState}
   */
  state = new HTMLEditorState();

  /**
   * Saves the preicous state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Object}
   */
  state_history = {};

  /**
   * Editor State
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {HTMLEditorState}
   */
  defaults = new HTMLEditorState();

  /**
   * Editor node element
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Node}
   */
  element;

  /**
   * Sets the editor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {NodeElement} target_element editor html element
   * @param {Object}      params         editor params
   *
   * @return {void}
   */
  set(target_element, params) {
    this.element = target_element;
    this.squire  = new Squire(target_element, params);
  }

  /**
   * Sets editor defaults
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setDefaults() {
    this.setFontFace(this.defaults.font);
    this.setFontSize(this.defaults.size);
  }

  /**
   * Sets editor font face
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} font font name
   *
   * @return {void}
   */
  setFontFace(font) {
    this.setStateHistory('font', font);
    this.setState('font', font);
    this.squire.setFontFace(
      this.options.getFontFamily(font)
    );
  }

  /**
   * Sets editor font size
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} size font size
   *
   * @return {void}
   */
  setFontSize(size) {
    this.setStateHistory('size', size);
    this.setState('size', size);
    this.squire.setFontSize(size);
  }

  /**
   * Toggles bold on/off
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  toggleBold() {
    this.setBold(!this.state.bold);
  }

  /**
   * Sets editor bold value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} value
   *
   * @return {void}
   */
  setBold(value) {
    this.setState('bold', value);
    this.setStateHistory('bold', value);
    (this.state.bold) ? this.squire.bold() : this.squire.removeBold();
  }

  /**
   * Toggles editor underline value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  toggleUnderline() {
    this.setUnderline(!this.state.underline);
  }

  /**
   * Sets editor underline value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} value
   *
   * @return {void}
   */
  setUnderline(value) {
    this.setState('underline', value);
    this.setStateHistory('underline', value);
    (this.state.underline ) ? this.squire.underline() : this.squire.removeUnderline();
  }

  /**
   * Set a specific HTML element or Range as the current selection in the Squire editor.
   * If the provided target_element is not a Range, it converts it
   * into a Range before setting the selection.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {HTMLElement|Range} target_element - The HTML element or Range
   * to set as the current selection.
   *
   * @return {void}
   */
  setSelection(target_element) {
    if (!(target_element instanceof Range)) {
      target_element = this.createHTMLRange(target_element);
    }
    this.squire.setSelection(target_element);
  }

  /**
   * Create a Range object that encompasses the provided HTML element.
   * A Range represents a fragment of a document that can
   * contain nodes and parts of text nodes.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {HTMLElement} target_element - The HTML element around which to create a Range.
   *
   * @return {Range} - The created Range object.
   */
  createHTMLRange(target_element) {
    const range = document.createRange();
    range.selectNode(target_element);
    return range;
  }


  /**
   * Toggles editor italic value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  toggleItalic() {
    this.setItalic(!this.state.italic);
  }

  /**
   * Sets editor italic value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} value
   *
   * @return {void}
   */
  setItalic(value) {
    this.setState('italic', value);
    this.setStateHistory('italic', value);
    (this.state.italic ) ? this.squire.italic() : this.squire.removeItalic();
  }

  /**
   * Toggles editor strikethrough value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  toggleStrikethrough() {
    this.setStrikethrough(!this.state.strike);
  }

  /**
   * Sets editor strikethrough value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} value
   *
   * @return {void}
   */
  setStrikethrough(value) {
    this.setState('strike', value);
    this.setStateHistory('strike', value);
    (this.state.strike ) ? this.squire.strikethrough() : this.squire.removeStrikethrough();
  }

  /**
   * Sets editor color value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} color
   *
   * @return {void}
   */
  setColor(color) {
    this.setStateHistory('color', color);
    this.setState('color', color);
    this.squire.setTextColor(color);
  }

  /**
   * Sets editor background color value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} color
   *
   * @return {void}
   */
  setBackgroundColor(color) {
    this.setStateHistory('background_color', color);
    this.setState('background_color', color);
    this.squire.setHighlightColor(color);
  }

  /**
   * Sets editor alignment value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} alignment
   *
   * @return {void}
   */
  setTextAlignment(alignment) {
    this.setState('alignment', alignment);
    this.squire.setTextAlignment(this.options.alignments[alignment]);
  }

  /**
   * Creates an ordered list
   *
   * @public
   *
   * @param {Boolean} value
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setOrderedList(value) {
    this.setState('ordered_list', value);
    this.setStateHistory('ordered_list', value);
    this.state.ordered_list ? this.squire.makeOrderedList() : this.squire.removeList();
  }

  /**
   * Toggles between ordered list on/off
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  toggleOrderedList() {
    this.setOrderedList(!this.state.ordered_list);
  }

  /**
   * Toggles between unordered list on/off
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  toggleUnorderedList() {
    this.setUnorderedList(!this.state.unordered_list);
  }

  /**
   * Creates an unordered list
   *
   * @public
   *
   * @param {Boolean} value
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setUnorderedList(value) {
    this.setState('unordered_list', value);
    this.setStateHistory('unordered_list', value);
    this.state.unordered_list ? this.squire.makeUnorderedList() : this.squire.removeList();
  }

  /**
   * Creates a code block
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setCode() {
    this.setState('code', true);
    this.squire.toggleCode()
  }

  /**
   * Creates/increases a quote block
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setIncreasedQuoteLevel() {
    this.setState('quote', true);
    this.squire.increaseQuoteLevel();
  }

  /**
   * Removes/decreases a code block
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setDecreasedQuoteLevel() {
    this.setState('quote', false);
    this.squire.decreaseQuoteLevel();
  }

  /**
   * Inserts images to the editor
   *
   * Returns an array with added images
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FilesEvent} images  images event files
   *
   * @return {void}
   */
  insertImages(images) {
    images.items.forEach((file) => {
      let fr = new FileReader();
      var ed = this.squire;
      fr.readAsDataURL(file);

      fr.onload = function(e) {
        ed.insertImage(e.target.result, {
          id: file.id,
        });
      };
    });
  }

  /**
   * Applies a link to the selection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Object}
   */
  insertLink(data) {
    let attr = {};

    if (data.text) {
      attr['data-text'] = data.text;
    }

    if (data.node_link && data.target.nodeName !== 'IMG') {
      data.node_link.setAttribute('href', data.href);
      return;
    }

    this.squire.makeLink(data.href, attr);
  }

  /**
   * Removes all links from selection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  removeLink() {
    this.squire.removeLink();
  }

  /**
   * Insert a template clone at the selections
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} template_id template id to clone
   * @param {String} element_id  adds an element id to the first child
   *
   * @return {DocumentFragment}
   */
  insertTemplate(template_id, element_id) {
    let html  = document.getElementById(template_id);
    let clone = html.content.cloneNode(true);
    clone.firstChild.setAttribute('id', element_id);
    this.insertElement(clone);
    return document.getElementById(element_id);
  }

  /**
   * Insert HTML at the selection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Node} html
   *
   * @return {Void}
   */
  insertHTML(html) {
    this.squire.insertHTML(html);
  }

  /**
   * Insert HTML at the selection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Node} html
   *
   * @return {Void}
   */
  insertElement(html, focus = true) {
    html = DOMPurify.sanitize(html, {
      ALLOW_UNKNOWN_PROTOCOLS: false,
      WHOLE_DOCUMENT: false,
      RETURN_DOM: true,
      RETURN_DOM_FRAGMENT: true,
      FORCE_BODY: false
    })

    this.squire.insertElement(html, false, focus);
  }

  /**
   * Sanitizes HTML
   * Removes all potentially harmful elements
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} html
   *
   * @return {String}
   */
  sanitizeHTML(html) {
    return DOMPurify.sanitize(html, {
      ALLOW_UNKNOWN_PROTOCOLS: false,
      WHOLE_DOCUMENT: false,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      FORCE_BODY: false
    })
  }

  /**
   * Removes an element by id
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} fn modification funcion
   *
   * @return {Object}
   */
  removeElement(id) {
    let element = document.getElementById(id);

    if (element) {
      element.remove();
    }
  }

  getRemovedTemplates(templates) {
    let removed = [];

    for (let template in templates) {
      if (!document.getElementById(template)) {
        removed.push(template);
      }
    }

    return removed;
  }

  /**
   * Applies editor undo
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setUndo() {
    this.squire.undo();
  }

  /**
   * Applies editor redo
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setRedo() {
    this.squire.redo();
  }

  /**
   * Replaces all editor contents
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {String}
   */
  setContents(html) {
    this.squire.setHTML(html);
  }

  /**
   * Gets Editor Contents
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {String}
   */
  getContents() {
    return this.squire.getHTML();
  }

  /**
   * Clears markup formatting
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  clearFormatting() {
    this.squire.removeAllFormatting();
    let range = this.squire.getSelection();

    if (range.commonAncestorContainer.nodeType === Node.TEXT_NODE) {
      if (range.commonAncestorContainer.parentNode) {
        range.commonAncestorContainer.parentNode.removeAttribute('style');
      }

      return;
    }

    range.commonAncestorContainer.removeAttribute('style');
  }

  /**
   * Add focus after the element
   * informed
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {HTMLElement} element
   *
   * @returns {void}
   */
  setFocusAfterElement(element){
    const range = new Range();

    range.setStartAfter(element);
    range.collapse(true);

    const selection = document.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    this.squire.focus();
  }

  /**
   * Sets a state history param value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}              type  state history type
   * @param {String|Boolean|null} value param value
   *
   * @return {void}
   */
  setStateHistory(type, value) {
    this.state_history[type] = value;
  }

  /**
   * Sets a state param value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}              type  state type
   * @param {String|Boolean|null} value param value
   *
   * @return {void}
   */
  setState(type, value) {
    if (value === undefined) {
      return;
    }

    if (this.state[type] === value) {
      return;
    }

    this.state[type] = value;
  }

  /**
   * Restores state to the saved state history
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  restoreStateHistory() {
    this.setFontFace(this.state_history['font']);
    this.setFontSize(this.state_history['size']);
    this.setColor(this.state_history['color']);
    this.setBold(this.state_history['bold']);
    this.setItalic(this.state_history['italic']);
    this.setUnderline(this.state_history['underline']);
    this.setStrikethrough(this.state_history['strike']);
    this.setBackgroundColor(this.state_history['background_color']);
  }

  /**
   * Returns if element selected is the fisrt element in the editor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  isFirstElement() {
    let selection = this.squire.getSelection();

    return (this.element.firstChild.contains(selection.startContainer) &&
            selection.commonAncestorContainer.parentNode === this.element
    );
  }

  /**
   * Monitors any state changes in the selected text and applies it to the editor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  monitorStateChanges() {
    let state = this.getPathState();

    if (state.selection === true) {
      return;
    }

    for (let type in state) {
      this.checkStateChange(type, state[type]);
    }
  }

  /**
   * Checks select text font state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} font selected text font
   *
   * @return {void}
   */
  checkStateChangeFont(font) {
    font = font ? font.split(',')[0].split('"').join('') : undefined;
    this.checkStateChange('font', font);
  }

  /**
   * Checks select text alignment
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} font selected text font
   *
   * @return {void}
   */
  checkStateChangeAlignement(alignment) {
    alignment = alignment ? alignment.split('-')[1] : undefined;
    this.checkStateChange('alignment', alignment);
  }

  /**
   * Compares current state with given state, and sets a new state when
   * state has changed
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}              type  state type
   * @param {String|Boolean|null} value param value
   *
   * @return {void}
   */
  checkStateChange(type, value) {
    if (value === undefined) {
      return;
    }

    if (value !== this.state[type]) {
      this.setState(type, value);
    }
  }

  /**
   * Returns current text state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {HTMLEditorState}
   */
  getPathState() {
    let path        = this.squire.getPath().split('.');
    let info        = this.squire.getFontInfo();
    let state       = new HTMLEditorState();
    state.selection = path[0] === '(selection)';

    if (state.selection) {
      return state;
    }

    path.forEach(element => {
      this.setPathAlignment(element, state);
    })

    state.font             = info.fontFamily ? info.fontFamily.split(',')[0].split('"').join('') : undefined;
    state.size             = info.fontSize;
    state.bold             = this.squire.hasFormat('B');
    state.italic           = this.squire.hasFormat('I');
    state.underline        = this.squire.hasFormat('U');
    state.strike           = this.squire.hasFormat('S');
    state.color            = info.color           || null;
    state.background_color = info.backgroundColor || null;
    state.ordered_list     = this.squire.hasFormat('OL');
    state.unordered_list   = this.squire.hasFormat('UL');
    state.quote            = this.squire.hasFormat('blockquote');
    state.code             = this.squire.hasFormat('pre') || this.squire.hasFormat('code');
    return state;
  }

  /**
   * Checks if element has aligment style applied
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setPathAlignment(element, state) {
    if (!element.includes('align')) {
      return;
    }

    state.alignment = element.split('>')[0];
  }

  /**
   * Returns selected link data when only the link node is selected
   *
   * Discards all empty nodes
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  getSelectedLinkData(clicked_node = {}) {
    let selection = this.getSelectedNodes();
    let range     = this.squire.getSelection();
    let data      = {
      node_link : '',
      node_text : '',
      text      : '',
      href      : '',
      target    : clicked_node.target || null,
      nodes : selection.length,
    }

    if (selection.length > 1 ) {
      return data;
    }

    let node = (selection[0].nodeType === Node.TEXT_NODE && selection[0].parentNode.nodeName === 'A')
             ? selection[0].parentNode
             : selection[0];


    data.node_link = this.getNodeByTag(node, 'A');
    data.node_text = this.getNodeLastChild(node);

    if (!data.node_link) {
      data.text = this.getNodeData(data.node_text);
      data.text = data.text.substring(range.startOffset, range.endOffset);
      return data;
    }

    data.href = data.node_link.getAttribute('href') || ''
    data.text = this.getNodeData(data.node_text);
    return data;
  }

  /**
   * Returns all selected root nodes
   *
   * Discards all empty nodes
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Node} node node tree
   *
   * @return {void}
   */
  getSelectedNodes() {
    let selection = this.squire.getSelection();
    let nodes     = [];

    const start_boundary = this.getAncestorNode(selection.startContainer, selection.commonAncestorContainer);
    const end_boundary   = this.getAncestorNode(selection.endContainer, selection.commonAncestorContainer);

    let node = start_boundary;

    while (node) {
      if (this.isNodeEmpty(node) === false) {
        nodes.push(node);
      }

      if (node === end_boundary) {
        break;
      }

      node = node.nextSibling;
    }

    if (nodes.length === 0) {
      nodes.push(start_boundary);
    }

    return nodes;
  }

  /**
   * Iterates the childs tree to retrieve the first node of given type
   *
   * If tree has more than one child returns undefined
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Node} node node tree
   * @param {Node} type node type
   *
   * @return {void}
   */
  getNodeByType(node, type) {
    while (node) {
      if (node.nodeType === type) {
        return node;
      }

      if (node.childNodes.lenght > 1) {
        return undefined;
      }

      node = node.childNodes[0];
    }
  }

  /**
   * Iterates the childs tree to retrieve the last child node
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Node} node node tree
   *
   * @return {void}
   */
  getNodeLastChild(node) {
    while (node) {
      if (!node.hasChildNodes()) {
        return node;
      }

      node = node.childNodes[0];
    }
  }

  /**
   * Iterates the childs tree to retrieve the first node of a given tag
   *
   * If tree has more than one child returns undefined
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Node} node node tree
   * @param {Node} type node type
   *
   * @return {void}
   */
  getNodeByTag(node, tag) {
    if (node.nodeName === tag) {
      return node;
    }

    if (!node.querySelector) {
      return null;
    }

    return node.querySelector(tag);
  }

  /**
   * Iterates the childs tree to retrieve the text node
   *
   * If tree has more than one child returns undefined
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Node} node node tree
   *
   * @return {void}
   */
  getNode(node) {
    while (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        return node;
      }

      if (node.childNodes.lenght > 1) {
        return undefined;
      }

      node = node.childNodes[0];
    }
  }

  /**
   * Iterates the childs tree to figure it out if any of the child nodes
   * has data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Node}    node  node tree
   * @param {Boolean} empty
   * @param {Number}  child
   *
   * @return {Boolean}
   */
  isNodeEmpty(node, empty = true, child = 0) {
    node = node.childNodes[child] || null;

    while (node) {
      if (empty === false) {
        return false;
      }

      if (node.hasChildNodes()) {
        empty = this.isNodeEmpty(node, empty, child++);
        node  = node.nextSibling;
        continue;
      }

      if (HTMLSquireEditor.LEAFS.has(node.nodeName)) {
        empty = false;
        break;
      }

      if (node.nodeType !== Node.TEXT_NODE) {
        node = node.nextSibling;
        continue;
      }

      let contents = node.data.trim();

      if (contents.length > 0 && contents !== "\u200B") {
        empty = false;
        break;
      }

      node = node.nextSibling;
    }

    return empty;
  }

  /**
   * Iterates through all node parents to figure out the first child node
   * of the the common ancestor
   *
   * If childs tree has more than one child returns false
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Node} node            current node
   * @param {Node} common_ancestor common ancestor
   *
   * @return {Node}
   */
  getAncestorNode = (node, common_ancestor) => {
    if (node === common_ancestor) {
      return node;
    }

    while (node) {
      if (node.parentNode === common_ancestor) {
        return node;
      }

      node = node.parentNode;
    }
  }

  /**
   * Replaces ZWS Character from string
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} string
   *
   * @return {String}
   */
  replaceZWS(string) {
    return string.replace("\u200B", '');
  }

  /**
   * Returns node text data, if data does n't exist returns alt attribute
   *
   * If alt doesn't exist either returns null
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Node} node
   *
   * @return {String}
   */
  getNodeData(node) {
    if (node.data) {
      return this.replaceZWS(node.data);
    }

    let alt = node.getAttribute('alt');

    if (alt) {
      return this.replaceZWS(alt);
    }

    return '';
  }

}